import React, { createRef, useEffect, useState, useMemo } from "react";
import ManagementForm from "./ManagementForm";
import { Grid, Button, IconButton, Snackbar, TextField, InputAdornment, Box, Typography } from "@mui/material";
import { useRef } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import SearchIcon from "@mui/icons-material/Search";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useFormik } from "formik";
import PrimaryLoader from "../../components/Loader/PrimaryLoader";
import axios from "axios";
import {
  getAccessToken,
  getFurnitureUrl,
  postManageFormUrl,
} from "../../utils/Utils";
import ConfirmDialog from "../../components/ConfirmDialog";

const Management = () => {
  const initialState = [
    {
      _id: "",
      room: "",
      furniture: [],
    },
  ];

  const [furnitureData, setFurnitureData] = useState(initialState);
  const [otherFurnitureData, setOtherFurnitureData] = useState([]);
  const [otherFurnitureTypes, setOtherFurnitureTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roomsAutoComplete, setRoomsAutoComplete] = useState([]);
  const [furnitureAutoCompleteData, setFurnitureAutoCompleteData] = useState(
    []
  );
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [openFurnitureModal, setOpenFurnitureModal] = useState(false);
  const [roomName, setRoomName] = useState("");
  const [openRoomModal, setOpenRoomModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedPanels, setExpandedPanels] = useState(new Set());
  const [manuallyExpandedPanels, setManuallyExpandedPanels] = useState(new Set());

  const roomRefs = useRef({});

  // Search filtering logic
  const filteredFurnitureDataWithSearch = useMemo(() => {
    if (!searchQuery.trim()) {
      setExpandedPanels(new Set());
      return furnitureData;
    }

    const query = searchQuery.toLowerCase();
    const newExpandedPanels = new Set();

    const filtered = furnitureData.map(room => ({
      ...room,
      furniture: room.furniture.map(category => {
        const matchedSubtypes = category.subtypes.filter(subtype =>
          subtype.name.toLowerCase().includes(query)
        );

        if (matchedSubtypes.length > 0) {
          newExpandedPanels.add(`${room.room}-${category.type}`);
        }

        return {
          ...category,
          subtypes: matchedSubtypes
        };
      }).filter(category => category.subtypes.length > 0)
    })).filter(room => room.furniture.length > 0);

    setExpandedPanels(newExpandedPanels);
    return filtered;
  }, [furnitureData, searchQuery]);

  // Accordion control handler
  const handleAccordionChange = (panelId) => (event, isExpanded) => {
    if (!searchQuery.trim()) {
      // Only allow manual control when not searching
      const newManuallyExpanded = new Set(manuallyExpandedPanels);
      if (isExpanded) {
        newManuallyExpanded.add(panelId);
      } else {
        newManuallyExpanded.delete(panelId);
      }
      setManuallyExpandedPanels(newManuallyExpanded);
    }
    // When searching, don't allow manual control
  };

  useEffect(() => {
    getFurnitureData();
  }, []);

  const getFurnitureData = async () => {
    setLoading(true);
    axios
      .get(getFurnitureUrl)
      .then((response) => {
        if (
          response.data.status &&
          response.data.message === "success" &&
          response.data.resultCode === 200
        ) {
          setFurnitureData(response.data.data.rooms);
          // Populate refs dynamically after data fetch
          const refs = response.data.data.rooms.reduce((acc, room) => {
            acc[room.room] = createRef();
            return acc;
          }, {});
          roomRefs.current = refs; // Update the current property of the ref
          const rooms = response.data.data.rooms.map((room) => ({
            id: room._id,
            label: room.room,
          }));
          setRoomsAutoComplete(rooms);
        }

        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setSnackbar({
          open: true,
          message: "Something went wrong",
        });
      });
  };

  const initialValues = {
    _id: "",
    room: { id: 0, label: "" },
    type: { id: 0, label: "" },
    name: "",
    price: "",
    subTypeId: "",
  };

  const formik = useFormik({
    initialValues,
    onSubmit: async (values, { resetForm }) => {
      const param = {
        room: values.room.label,
        furniture: [
          {
            type: values.type.label,
            subtypes: [
              {
                name: values.name,
                price: values.price,
                quantity: 0,
                _id: formik.values._id === "" ? undefined : values.subTypeId,
              },
              ...otherFurnitureData,
            ],
          },
          ...otherFurnitureTypes,
        ],
      };
      if (formik.values._id === "") {
        const filteredRoom = furnitureData.find(
          (room) => room._id === values.room.id
        );
        const filteredFurniture = filteredRoom.furniture.find(
          (furniture) => furniture._id === values.type.id
        );
        const filteredSubType = filteredFurniture.subtypes.find(
          (subtype) => subtype._id === values.subTypeId
        );
        const filteredTypes = [...otherFurnitureTypes].filter(
          (item) => item._id !== filteredFurniture._id
        );
        const createParam = {
          room: values.room.label,
          furniture: [
            {
              type: values.type.label,
              subtypes: [
                ...filteredFurniture.subtypes,
                {
                  name: values.name,
                  price: values.price,
                  quantity: 0,
                  _id: formik.values._id === "" ? undefined : values.subTypeId,
                },
              ],
            },
            ...filteredTypes,
          ],
        };
        const res = await axios.put(
          `${postManageFormUrl}/${values.room.id}`,
          createParam,
          getAccessToken()
        );
        if (res.data.status) {
          setSnackbar({
            open: true,
            message: "Submitted successfully!",
          });
          await getFurnitureData();
          resetForm();
        } else {
          setSnackbar({
            open: true,
            message: "Something went wrong please try again!",
          });
        }
      } else {
        const res = await axios.put(
          `${postManageFormUrl}/${values._id}`,
          param,
          getAccessToken()
        );
        if (res.data.status) {
          setSnackbar({
            open: true,
            message: "Updated successfully!",
          });
          await getFurnitureData();
          resetForm();
        } else {
          setSnackbar({
            open: true,
            message: "Something went wrong please try again!",
          });
        }
      }
    },
  });

  const scrollToRoom = (roomName) => {
    roomRefs.current[roomName].current.scrollIntoView({ behavior: "smooth" });
  };

  const handleEdit = (room, category, subtype, otherSubTypes) => {
    const otherFurnitureTypes = room.furniture.filter(
      (item) => item._id !== category._id
    );
    const others = otherSubTypes.filter((type) => type._id !== subtype._id);
    setOtherFurnitureTypes(otherFurnitureTypes);
    setOtherFurnitureData(others);
    formik.setFieldValue("_id", room._id);
    formik.setFieldValue("room", { id: room._id, label: room.room });
    formik.setFieldValue("type", { id: category._id, label: category.type });
    formik.setFieldValue("name", subtype.name);
    formik.setFieldValue("price", subtype.price);
    formik.setFieldValue("subTypeId", subtype._id);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = (room, category, subtype, otherSubTypes) => {
    const others = otherSubTypes.filter((type) => type._id !== subtype._id);
    const otherFurnitureTypes = room.furniture.filter(
      (item) => item._id !== category._id
    );
    setOtherFurnitureTypes(otherFurnitureTypes);
    setOtherFurnitureData(others);
    formik.setFieldValue("_id", room._id);
    formik.setFieldValue("room", { id: room._id, label: room.room });
    formik.setFieldValue("type", { id: category._id, label: category.type });
    formik.setFieldValue("name", subtype.name);
    formik.setFieldValue("price", subtype.price);
    formik.setFieldValue("subTypeId", subtype._id);
    setOpenDialog(true);
  };

  const onDelete = async () => {
    const param = {
      room: formik.values.room.label,
      furniture: [
        {
          type: formik.values.type.label,
          subtypes: [...otherFurnitureData],
        },
        ...otherFurnitureTypes,
      ],
    };
    const res = await axios.put(
      `${postManageFormUrl}/${formik.values._id}`,
      param,
      getAccessToken()
    );
    if (res.data.status) {
      setSnackbar({
        open: true,
        message: "Deleted successfully!",
      });
      await getFurnitureData();
    } else {
      setSnackbar({
        open: true,
        message: "Something went wrong please try again!",
      });
    }
    formik.resetForm();
    setOpenDialog(false);
  };

  const handleRoomSelect = (room) => {
    formik.setFieldValue("room", room);
    const filteredList = furnitureData.find(
      (furniture) => furniture._id === room.id
    );
    setOtherFurnitureTypes(filteredList.furniture);
    const furnitureAutoCompleteList = filteredList.furniture.map((item) => ({
      id: item._id,
      label: item.type,
    }));
    setFurnitureAutoCompleteData(furnitureAutoCompleteList);
  };

  const furnitureFormik = useFormik({
    initialValues: {
      furnitureName: "",
      room: { id: 0, label: "" },
    },
    onSubmit: async (values, { resetForm }) => {
      const param = {
        room: values.room.label,
        furniture: [
          {
            type: values.furnitureName,
            subtypes: [],
          },
        ],
      };
      const res = await axios.put(
        `${postManageFormUrl}/${values.room.id}`,
        param,
        getAccessToken()
      );
      if (res.data.status) {
        setSnackbar({
          open: true,
          message: "Submitted successfully!",
        });
        await getFurnitureData();
        resetForm();
        setOpenFurnitureModal(false);
      } else {
        setSnackbar({
          open: true,
          message: "Something went wrong please try again!",
        });
      }
    },
  });

  const handleModalRoomSelect = (room) => {
    furnitureFormik.setFieldValue("room", room);
  };

  const handleFurnitureSelect = (furniture) => {
    formik.setFieldValue("type", furniture);
  };

  const handleRoomSubmit = async () => {
    const param = {
      room: roomName,
      furniture: [],
    };
    const res = await axios.post(
      `${postManageFormUrl}`,
      param,
      getAccessToken()
    );
    if (res.data.status) {
      setSnackbar({
        open: true,
        message: "Submitted successfully!",
      });
      await getFurnitureData();
      setRoomName("");
      setOpenRoomModal(false);
    } else {
      setSnackbar({
        open: true,
        message: "Something went wrong please try again!",
      });
    }
  };

  return (
    <Grid sx={{ margin: "1rem" }}>
      <ManagementForm
        formik={formik}
        handleRoomSelect={handleRoomSelect}
        handleFurnitureSelect={handleFurnitureSelect}
        rooms={roomsAutoComplete}
        furniture={furnitureAutoCompleteData}
        furnitureFormik={furnitureFormik}
        handleModalRoomSelect={handleModalRoomSelect}
        openFurnitureModal={openFurnitureModal}
        setOpenFurnitureModal={setOpenFurnitureModal}
        handleRoomSubmit={handleRoomSubmit}
        roomName={roomName}
        setRoomName={setRoomName}
        openRoomModal={openRoomModal}
        setOpenRoomModal={setOpenRoomModal}
      />
      <Grid
        sx={{
          padding: "1rem",
          border: "1px solid #1976d2",
          borderRadius: "10px",
          marginTop: "1rem",
          marginBottom: "1rem",
        }}
      >
        <Typography style={{ color: "#1976d2" }} variant="h5" gutterBottom>
          Items
        </Typography>
        <TextField
          fullWidth
          placeholder="Search items..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ marginBottom: "1rem" }}
        />
        <Grid mt={2}>
          <Grid container spacing={2} sx={{ marginBottom: "1rem" }}>
            {filteredFurnitureDataWithSearch && filteredFurnitureDataWithSearch.map((room) => (
              <Grid item key={room.room}>
                <Button
                  variant="contained"
                  onClick={() => scrollToRoom(room.room)}
                >
                  {room.room}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Grid>
        {filteredFurnitureDataWithSearch && filteredFurnitureDataWithSearch.map((room) => (
          <div key={room.room} ref={roomRefs.current[room.room]}>
            <Typography variant="h6" gutterBottom>
              {room.room}
            </Typography>
            {room.furniture.map((category) => {
              const panelId = `${room.room}-${category.type}`;
              const isExpanded = searchQuery.trim() 
                ? expandedPanels.has(panelId) // Auto-expand when searching
                : manuallyExpandedPanels.has(panelId); // Manual control when not searching
              const query = searchQuery.toLowerCase();
              
              return (
                <Accordion 
                  key={category.type} 
                  expanded={isExpanded}
                  onChange={handleAccordionChange(panelId)}
                  sx={{ marginBottom: 2 }}
                >
                  <AccordionSummary
                    expandIcon={<ArrowDropDownIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                  >
                    <Typography>{category.type}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    {category.subtypes.map((subtype) => {
                      const isHighlighted = searchQuery.trim() && 
                        subtype.name.toLowerCase().includes(query);
                      
                      return (
                        <Grid
                          key={subtype._id}
                          container
                          spacing={2}
                          alignItems="center"
                          sx={{
                            backgroundColor: isHighlighted ? '#e3f2fd' : 'transparent',
                            padding: isHighlighted ? '8px' : '10px',
                            borderRadius: isHighlighted ? '4px' : '0',
                            border: isHighlighted ? '1px solid #1976d2' : 'none',
                            marginBottom: '4px'
                          }}
                        >
                          <Grid
                            item
                            xs={12}
                            sx={{
                              display: "flex",
                            }}
                          >
                            <Grid xs={9}>
                              <Typography 
                                style={{
                                  fontWeight: isHighlighted ? 'bold' : 'normal',
                                  color: isHighlighted ? '#1976d2' : 'inherit'
                                }}
                              >
                                {`${subtype.name} - ₹${subtype.price}`}
                              </Typography>
                            </Grid>
                            <Grid xs={3}>
                              <IconButton
                                sx={{ color: "#06402B" }}
                                onClick={() => {
                                  handleEdit(
                                    room,
                                    category,
                                    subtype,
                                    category.subtypes
                                  );
                                }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                sx={{ color: "#ea1520" }}
                                onClick={() =>
                                  handleDelete(
                                    room,
                                    category,
                                    subtype,
                                    category.subtypes
                                  )
                                }
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Grid>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </div>
        ))}
      </Grid>
      <PrimaryLoader openModal={loading} />
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={snackbar.open}
        onClose={() => {
          setSnackbar({ ...snackbar, open: false });
        }}
        message={snackbar.message}
      />
      <ConfirmDialog
        open={openDialog}
        setOpen={() => setOpenDialog(false)}
        handleAccept={onDelete}
      />
    </Grid>
  );
};

export default Management;
